import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Grid from "../components/Grid"; // Import the Grid component
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";

// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Customers({ onAddCustomerClick }) {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort] = useState({
    field: "customer_name",
    direction: "asc",
  });
  const [selectedSearchField, setSelectedSearchField] =
    useState("customer_name"); // New state for search field
  const [userRole, setUserRole] = useState(""); // State to store user role
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [messageBody, setMessageBody] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState([]); // State to store selected customers
  const [selectAll, setSelectAll] = useState(false);
  const [imageFile, setImageFile] = useState(null); // State to store image file
  const [imagePreview, setImagePreview] = useState(null); // State to store image preview URL
  const [organizationContactNo, setOrganizationContactNo] = useState(""); // State to store organization contact number
  const [organizationContactName, setOrganizationContactName] = useState("");


      const fetchOrganizationSettings = async () => {
        try {
          const token = sessionStorage.getItem("token");
          if (!token) {
            toast.error("Authentication token is missing. Please log in again.");
            return;
          }
    
          const response = await axios.get(`${API_BASE_URL}/organizations/getSettings`, {
            headers: { Authorization: `Bearer ${token}` },
          });
    
          setOrganizationContactNo(response.data.contact_number);
          setOrganizationContactName(response.data.contact_name);
        } catch (error) {
          toast.error("Error fetching organization settings.");
        }
      };

  useEffect(() => {
    // Fetch the logged-in user's role
    const fetchUserRole = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setUserRole(response.data.role);
      } catch (error) {
        console.error("Error fetching user role:", error);
        toast.error("Error fetching user role");
      }
    };    

    // Fetch customers from the API when the component mounts
    const fetchCustomers = async () => {
      try {
        const token = sessionStorage.getItem("token"); // Retrieve token from sessionStorage
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/customers/`, {
          headers: {
            Authorization: `Bearer ${token}`, // Include token in Authorization header
          },
        });

        
        // Map the customers to include an id field
        const customersWithId = response.data.customers.map((customer) => ({
          ...customer,
          id: customer.customer_code, // Assign _id to id
        }));
        setCustomers(customersWithId); // Set customers with id field
      } catch (error) {
        console.error("Error fetching customers:", error);
        toast.error("Error fetching customers");
      }
    };


    fetchCustomers();
    fetchUserRole();
    fetchOrganizationSettings(); // Fetch organization settings to get contact number
  }, []);

  const handleCustomerClick = (customer) => {
    // Ensure that the selected customer contains all necessary fields
    setSelectedCustomer({
      ...customer,
      customer_name: customer.customer_name || "",
      cus_address: customer.cus_address || "",
      cus_mobileno: customer.cus_mobileno || "",
      cus_email: customer.cus_email || "",
      customer_type: customer.customer_type || "",
      status: customer.status || "Active",
    });
  };

  const handleClosePopup = () => {
    setSelectedCustomer(null);
  };

  // Filter and sort the Customers
  const filteredCustomers = customers
    .filter((customer) => {
      switch (selectedSearchField) {
        case "customer_code":
          return (
            customer.customer_code &&
            customer.customer_code
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );
        case "customer_name":
          return (
            customer.customer_name &&
            customer.customer_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );
        case "cus_email":
          return (
            customer.cus_email &&
            customer.cus_email.toLowerCase().includes(searchTerm.toLowerCase())
          );
        case "customer_type":
          return (
            customer.customer_type &&
            customer.customer_type
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );

        case "cus_mobileno":
          return (
            customer.cus_mobileno &&
            customer.cus_mobileno
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );
        case "cus_address":
          return (
            customer.cus_address &&
            customer.cus_address
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );
        case "status":
          return (
            customer.status &&
            customer.status.toLowerCase().includes(searchTerm.toLowerCase())
          );

        default:
          return false;
      }
    })
    .sort((a, b) => {
      if (a[sort.field] < b[sort.field])
        return sort.direction === "asc" ? -1 : 1;
      if (a[sort.field] > b[sort.field])
        return sort.direction === "asc" ? 1 : -1;
      return 0;
    });

  const handleDownloadExcel = async () => {
  const dataToExport =
    searchTerm.trim().length > 0 ? filteredCustomers : customers;

  if (dataToExport.length === 0) {
    toast.warn("No customer data to export.");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Customer List");

  const headerRow = [
    "Customer Code",
    "Customer Name",
    "Email",
    "Mobile No",
    "Address",
    "Customer Type",
    "Status",
    ...(userRole === "Main Admin" ? ["Organization"] : []),
  ];

  worksheet.addRow(headerRow);

  const header = worksheet.getRow(1);
  header.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD966" },
    };
    cell.font = { bold: true };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  dataToExport.forEach((customer) => {
    const row = [
      customer.customer_code || "N/A",
      customer.customer_name || "N/A",
      customer.cus_email || "N/A",
      customer.cus_mobileno || "N/A",
      customer.cus_address || "N/A",
      customer.customer_type || "N/A",
      customer.status || "N/A",
    ];

    if (userRole === "Main Admin") {
      row.push(customer.organization?.organization_name || "N/A");
    }

    worksheet.addRow(row);
  });

  worksheet.columns.forEach((column) => {
    column.width = 20;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "customers.xlsx");
};


  const columns = [
    {
      field: "select",
      headerName: "Select",
      width: 100,
      renderHeader: () => (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={handleSelectAllChange}
        />
      ),
      renderCell: (params) => (
        <input
          type="checkbox"
          checked={selectedCustomers.some(
            (customer) => customer.customer_code === params.row.customer_code
          )}
          onChange={(e) => handleCustomerSelection(e, params.row)}
        />
      ),
    },    
    { field: "customer_code", headerName: "ID", flex: 1 }, // Use id here
    { field: "customer_name", headerName: "Customer Name", flex: 1 },
  ];

  // Add organization column only for Main Admin (after Product Name)
  if (userRole === "Main Admin") {
    columns.push({
      field: "organization_name",
      headerName: "Organization",
      flex: 1,
      renderCell: (params) => (
        <span>
          {params.row.organization
            ? params.row.organization.organization_name
            : "N/A"}
        </span>
      ),
    });
  }

  // Add remaining columns
  columns.push(
    { field: "cus_address", headerName: "Customer Address", flex: 1 },
    { field: "cus_mobileno", headerName: "Mobile No", flex: 1 },
    { field: "cus_email", headerName: "Email", flex: 1 },
    { field: "customer_type", headerName: "Customer Type", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const status = params.row.status;
        const statusClass =
          status === "Active"
            ? "bg-[#388E3C] text-white"
            : "bg-[#E57373] text-white";

        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <div className="flex justify-left items-left w-10 h-10">
          <button
            onClick={() => handleCustomerClick(params.row)}
            className="bg-[#28ad4b] text-white px-2 py-1 flex items-center rounded-lg"
          >
            <FaEdit className="text-white mr-2" />
            Edit
          </button>
        </div>
      ),
    }
  );

  useEffect(() => {
    if (
      filteredCustomers.length > 0 &&
      selectedCustomers.length === filteredCustomers.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedCustomers, filteredCustomers]);

  const handleUpdateCustomer = async () => {
    try {
      const token = sessionStorage.getItem("token"); // Retrieve token from sessionStorage
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }

      const updatedCustomerData = {
        ...selectedCustomer,
        whatsapp_allowed:
          selectedCustomer.whatsapp_allowed !== undefined
            ? selectedCustomer.whatsapp_allowed
            : true,
      };

      await axios.put(
        `${API_BASE_URL}/customers/update/${selectedCustomer.customer_code}`,
        updatedCustomerData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Include token in Authorization header
          },
        }
      );
      toast.success("Customer Updated Succesfully");
      setSelectedCustomer(null);

      // Refresh the customer list
      const response = await axios.get(`${API_BASE_URL}/customers`, {
        headers: {
          Authorization: `Bearer ${token}`, // Include token in Authorization header
        },
      });

      // Map the products to include an id field
      const customersWithId = response.data.customers.map((customer) => ({
        ...customer,
        id: customer.customer_code,
      }));

      setCustomers(customersWithId);
    } catch (error) {
      console.error("Error updating customers:", error);
      toast.error("Error Updating Customer");
    }
  };

  const handleCustomerSelection = (e, customer) => {
    setSelectedCustomers((prev) => {
      if (e.target.checked) {
        if (!prev.some((c) => c.customer_code === customer.customer_code)) {
          return [...prev, customer];
        }
        return prev;
      } else {
        return prev.filter((c) => c.customer_code !== customer.customer_code);
      }
    });
  };

  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
  
    if (isChecked) {
      setSelectedCustomers(filteredCustomers); // Select only filtered (visible) rows
    } else {
      setSelectedCustomers([]);
    }
  };
  

  const handleSendWhatsAppClick = async (selectedCustomers, message) => {

    try {
      const token = sessionStorage.getItem("token"); // Retrieve token from sessionStorage
      if (!token) {
        toast.error("Authentication token is missing. Pleas`e log in again.");
        return;
      }

      if (!selectedCustomers || selectedCustomers.length === 0) {
        toast.error("Please select at least one customer to send a message.");
        return;
      }

      let uploadedImageUrl = null;

      if (imageFile) {
        const formData = new FormData();
        formData.append("image", imageFile); // Append the image file to the form data

        const uploadResponse = await axios.post(
          `${API_BASE_URL}/sendwhatsapp/uploadImage`, 
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`, 
            },
          }
        );

        uploadedImageUrl = uploadResponse.data.imageUrl; // Assuming the response contains the image URL
      }

      for (const customer of selectedCustomers) {
        if (!customer.cus_mobileno) {
          toast.error(
            `Customer ${customer.customer_name} does not have a mobile number.`
          );

          continue;
        }        

        const whatsappResponse = await axios.post(`${API_BASE_URL}/sendwhatsapp/sendwhatsapp`, {
          phoneNumber: customer.cus_mobileno,
          customerName: customer.customer_name,
          organizationContactNumber: organizationContactNo,
          organizationContactName: organizationContactName,
          messages: message,
          messageType: "newBroadcastwhtasappmessage",
          imageUrl: uploadedImageUrl,
        });
    
    
          if (whatsappResponse.data.success) {
            toast.success(
                `Message sent successfully to ${customer.customer_name}`
            );
        } else {
            toast.error(`Failed to send message to ${customer.customer_name}`);
            console.error(
                `Failed to send message to ${customer.customer_name}`
            );
        }

        await axios.post(
          `${API_BASE_URL}/broadcastMessages/add`,
          {
            customer_id: customer._id,
            message: message,
            organization: customer.organization?._id, // Ensure organization is populated
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
  
      }
      
      setSelectedCustomers([]);
      setMessageBody("");
      setImageFile(null);
      setImagePreview(null); // Reset image preview
      setShowMessagePopup(false);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message.");
    }
  }

  return (
    <div className="flex h-screen">
      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Customers</h1>
          <div className="flex gap-4">
          {/* Add Customer Button */}
            <div>
              <button
                onClick={onAddCustomerClick}
                className="bg-green-700 text-white px-4 py-2 rounded-lg"
              >
                Add Customer
              </button>
            </div>

            {/* Send WhatsApp Button */}
            <div>
              {(userRole === "Outlet User" || userRole === "Company Admin" || userRole === "Farm User") && (
                <button
                onClick={() => {
                  if (selectedCustomers.length === 0) {
                    toast.error("Please select at least one customer to send a message.");
                    return;
                  }else {
                  }
                  setShowMessagePopup(true);
                }}
                className="bg-green-700 text-white px-4 py-2 rounded-lg"
              >
                Send WhatsApp
              </button>
              )}
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="customer_name">Customer Name</option>
                <option value="customer_code">Code</option>
                <option value="cus_email">Email</option>
                <option value="cus_mobileno">Mobile No</option>
                <option value="cus_address">Address</option>
                <opion value="status">Status</opion>
                <option value="customer_type">Customer Type</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Batch here"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>          

          {/* customer Grid */}
          <Grid 
            rows={filteredCustomers} 
            columns={columns} 
            getRowId={(row) => row.customer_code}
            checkboxSelection={false} // disable default checkbox
          />
        </div>

        {/* Popup for Customer Details */}
        {selectedCustomer && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <div className="flex mb-4">
                <div className="w-1/2 pl-4">
                  <h2 className="text-xl font-semibold mb-4">
                    {selectedCustomer.customer_name}
                  </h2>
                  {/* customer address */}
                  <div className="mb-4">
                    <label className="block text-gray-700">
                      Customer Address
                    </label>
                    <input
                      type="text"
                      value={selectedCustomer.cus_address}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          cus_address: e.target.value,
                        })
                      }
                    />
                  </div>
                  {/* customer mobile No */}
                  <div className="mb-4">
                    <label className="block text-gray-700">
                      Customer MobileNo
                    </label>
                    <input
                      type="text"
                      value={selectedCustomer.cus_mobileno || ""}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          cus_mobileno: e.target.value,
                        })
                      }
                    />
                  </div>
                  {/* customer email */}
                  <div className="mb-4">
                    <label className="block text-gray-700">
                      Customer Email Address
                    </label>
                    <input
                      type="text"
                      value={selectedCustomer.cus_email}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          cus_email: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700">Customer Type</label>
                    <select
                      value={selectedCustomer.customer_type}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          customer_type: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Customer type</option>
                      <option value="Distributor">Distributor</option>
                      <option value="Dealer">Dealer</option>
                      <option value="Consumer">Consumer</option>
                    </select>
                  </div>
                  {/* Status Dropdown */}
                  <div className="mb-4">
                    <label className="block text-gray-700">Status</label>
                    <select
                      value={selectedCustomer.status}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          status: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="mb-4 flex items-center">
                    <input
                      type="checkbox"
                      id="whatsappAllowed"
                      className="mr-2 h-5 w-5"
                      checked={selectedCustomer.whatsapp_allowed ?? true} // default true if undefined
                      onChange={(e) =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          whatsapp_allowed: e.target.checked,
                        })
                      }
                    />
                    <label htmlFor="whatsappAllowed" className="text-gray-700">
                      Allow WhatsApp messages for sending request orders
                    </label>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleUpdateCustomer}
                      className="bg-blue-500 text-white px-4 py-2 rounded-lg"
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Popup for WhatsApp Message */}
        {showMessagePopup && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative">
              <button
                onClick={() => setShowMessagePopup(false)}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <h2 className="text-xl font-semibold mb-4">Send WhatsApp Message</h2>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Message</label>
                <textarea
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none"
                  placeholder="Type your message here..."
                />
              </div>
              {/* Image Upload Input */}
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Upload Image</label>
                {!imageFile ? (
                  <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setImageFile(file);
                      setImagePreview(URL.createObjectURL(file));
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                ) : (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full max-h-60 object-contain rounded border border-gray-300"
                    />
                    <button
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                      className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded hover:bg-red-800"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowMessagePopup(false);
                    setMessageBody("");
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSendWhatsAppClick(selectedCustomers, messageBody)}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Customers;
